import { useCallback, useEffect, useState } from 'react'
import { api, ApiError, type Account, type ApiKey } from '../api'

const STATUS_LABEL: Record<ApiKey['status'], string> = {
  active: 'активен',
  revoked: 'отозван',
}

export function Dashboard({ account }: { account: Account }) {
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [maxKeys, setMaxKeys] = useState(10)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [justCreated, setJustCreated] = useState<ApiKey | null>(null)

  const load = useCallback(async () => {
    try {
      const { keys, limits } = await api.me()
      setKeys(keys); setMaxKeys(limits.maxKeys); setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить ключи.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function revoke(k: ApiKey) {
    if (!confirm(`Отозвать «${k.name}»? Токен сразу перестанет работать.`)) return
    try { await api.revokeKey(k.id); await load() } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось отозвать.')
    }
  }
  async function remove(k: ApiKey) {
    if (!confirm('Удалить ключ навсегда?')) return
    try { await api.deleteKey(k.id); await load() } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось удалить.')
    }
  }

  const activeCount = keys.filter((k) => k.status === 'active').length

  return (
    <>
      <div className="section-title">
        <div>
          <h2>API-ключи</h2>
          <span className="muted">{account.email} · {activeCount}/{maxKeys} активных</span>
        </div>
        <NewKeyButton disabled={activeCount >= maxKeys} onCreated={(k) => { setJustCreated(k); load() }} setError={setError} />
      </div>

      {error && <div className="alert err">{error}</div>}

      {loading ? (
        <div className="empty"><span className="spin" /></div>
      ) : keys.length === 0 ? (
        <div className="card panel empty">
          <p>Пока нет ключей. Создайте первого бота, чтобы получить токен.</p>
        </div>
      ) : (
        <div className="keys">
          {keys.map((k) => (
            <KeyRow key={k.id} k={k} onRevoke={() => revoke(k)} onDelete={() => remove(k)} />
          ))}
        </div>
      )}

      {justCreated && <NewKeyResult apiKey={justCreated} onClose={() => setJustCreated(null)} />}
    </>
  )
}

function KeyRow({ k, onRevoke, onDelete }: { k: ApiKey; onRevoke: () => void; onDelete: () => void }) {
  const [revealed, setRevealed] = useState(false)
  const [copied, setCopied] = useState(false)
  const masked = k.token.slice(0, 12) + '·'.repeat(14) + k.token.slice(-6)

  function copy() {
    navigator.clipboard.writeText(k.token).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500) })
  }

  return (
    <div className="card keyrow">
      <div className="keymeta">
        <div className="keyname">
          {k.name}
          <span className={`badge ${k.status}`}>{STATUS_LABEL[k.status]}</span>
          {k.botUsername && <span className="muted mono" style={{ fontSize: 12.5, fontWeight: 400 }}>@{k.botUsername}</span>}
        </div>
        {k.description && <div className="muted" style={{ fontSize: 13, margin: '2px 0 6px' }}>{k.description}</div>}
        {k.status === 'active' && (
          <div className="keysecret copyable" onClick={copy} title="Нажмите, чтобы скопировать">
            {revealed ? k.token : masked}
          </div>
        )}
      </div>
      <div className="actions">
        {k.status === 'active' && (
          <>
            <button className="btn sm" onClick={() => setRevealed((v) => !v)}>{revealed ? 'Скрыть' : 'Показать'}</button>
            <button className="btn sm primary" onClick={copy}>{copied ? 'Скопировано ✓' : 'Копировать'}</button>
            <button className="btn sm danger" onClick={onRevoke}>Отозвать</button>
          </>
        )}
        {k.status === 'revoked' && <button className="btn sm ghost" onClick={onDelete}>Удалить</button>}
      </div>
    </div>
  )
}

function NewKeyButton({ disabled, onCreated, setError }: { disabled: boolean; onCreated: (k: ApiKey) => void; setError: (s: string | null) => void }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    setBusy(true); setError(null)
    try {
      const { key } = await api.createKey(name.trim(), description.trim())
      setOpen(false); setName(''); setDescription('')
      onCreated(key)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось создать ключ.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button className="btn primary" onClick={() => setOpen(true)} disabled={disabled}>+ Новый ключ</button>
      {open && (
        <div className="overlay" onClick={() => !busy && setOpen(false)}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
            <h3>Новый ключ</h3>
            <p className="muted" style={{ margin: '0 0 16px', fontSize: 13 }}>Задайте боту имя и, при желании, описание.</p>
            <div className="field">
              <label>Имя</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Бот поддержки" autoFocus maxLength={60} required />
            </div>
            <div className="field">
              <label>Описание <span className="muted">(необязательно)</span></label>
              <textarea className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Что делает бот" rows={3} maxLength={280} style={{ resize: 'vertical' }} />
            </div>
            <div className="modal-actions">
              <button className="btn primary block" type="submit" disabled={busy}>{busy ? <span className="spin" /> : 'Создать ключ'}</button>
              <button className="btn block" type="button" onClick={() => setOpen(false)} disabled={busy}>Отмена</button>
            </div>
          </form>
        </div>
      )}
    </>
  )
}

function NewKeyResult({ apiKey, onClose }: { apiKey: ApiKey; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  function copy() {
    navigator.clipboard.writeText(apiKey.token).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500) })
  }
  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>«{apiKey.name}» создан</h3>
        <p className="muted" style={{ margin: '0 0 14px', fontSize: 13 }}>
          Вот ваш ключ. Скопируйте его сейчас — позже его можно снова показать в консоли.
          {apiKey.botUsername && <> Юзернейм бота: <span className="mono">@{apiKey.botUsername}</span>.</>}
        </p>
        <div className="codebox" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{apiKey.token}</div>
        <div className="modal-actions">
          <button className="btn primary block" onClick={copy}>{copied ? 'Скопировано ✓' : 'Скопировать ключ'}</button>
          <button className="btn block" onClick={onClose}>Готово</button>
        </div>
      </div>
    </div>
  )
}
